import type { FunctionalComponent } from "vue";
import { Message } from "../message";
import { ToolCallIndicator } from "../tool-call-indicator";
import type {
  IActiveToolCall,
  IAssistantMessageAttachment,
} from "~/composables/useAssistant/types/message";
import Icon from "components/icon";

const isImageMime = (mime?: string): boolean =>
  !!mime && mime.toLowerCase().startsWith("image/");

const truncateMiddle = (s: string, max = 28): string => {
  if (s.length <= max) return s;
  const head = s.slice(0, Math.ceil(max / 2) - 1);
  const tail = s.slice(s.length - Math.floor(max / 2) + 1);
  return `${head}…${tail}`;
};

const AttachmentChip: FunctionalComponent<{ attachment: IAssistantMessageAttachment }> = (props) => {
  const { attachment } = props;
  const image = isImageMime(attachment.mime);
  return (
    <div
      class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/70 border border-primary-200/60 text-gray-700"
      title={attachment.name}
    >
      <span class="flex-shrink-0 w-4 h-4 rounded-md bg-primary-100 text-primary-600 inline-flex items-center justify-center">
        <Icon
          size="0.5rem"
          fill="currentColor"
          src={async () => import("assets/img/svg/icons/paper-clip.svg?raw")}
        />
      </span>
      <div class="min-w-0 flex flex-col leading-tight">
        <span class="text-[11px] font-medium font-['Inter'] truncate">
          {truncateMiddle(attachment.name)}
        </span>
        <span class="text-[9px] uppercase tracking-wider text-gray-400 font-['Inter']">
          {image ? "Image" : (attachment.mime?.split("/")[1] || "File")}
        </span>
      </div>
    </div>
  );
};

const ImageThumb: FunctionalComponent<{ attachment: IAssistantMessageAttachment }> = (props) => {
  const { attachment } = props;
  if (!attachment.previewUrl) return null;
  return (
    <div class="relative inline-block rounded-lg overflow-hidden bg-white/60 ring-1 ring-primary-200/60 shadow-[0_1px_2px_rgba(15,23,42,0.06)]" title={attachment.name}>
      <img
        src={attachment.previewUrl}
        alt={attachment.name}
        class="block max-w-[220px] max-h-[180px] object-cover"
      />
    </div>
  );
};

const AttachmentList: FunctionalComponent<{ attachments: IAssistantMessageAttachment[] }> = (props) => {
  if (!props.attachments || props.attachments.length === 0) return null;
  const images = props.attachments.filter((a) => isImageMime(a.mime) && !!a.previewUrl);
  const others = props.attachments.filter((a) => !(isImageMime(a.mime) && !!a.previewUrl));
  return (
    <div class="mt-2 pt-2 border-t border-primary-200/50 flex flex-col gap-2">
      {images.length > 0 ? (
        <div class="flex flex-wrap gap-1.5">
          {images.map((a) => (
            <ImageThumb key={a.id} attachment={a} />
          ))}
        </div>
      ) : null}
      {others.length > 0 ? (
        <div class="flex flex-col gap-1.5">
          {others.map((a) => (
            <AttachmentChip key={a.id} attachment={a} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const MessageBubble = defineComponent<{
  text: MaybeRef<string>;
  image?: FunctionalComponent<any>;
  senderRole: string;
  chunk?: boolean;
  thinking?: MaybeRef<boolean>;
  stream?: boolean;
  toolCall?: IActiveToolCall;
  attachments?: IAssistantMessageAttachment[];
}>(
  (props) => {
    const text = computed(() => unref(props.text));
    const thinking = computed(() => unref(props.thinking));

    return () => {
      const ImageComponent = props.image;
      const hasAttachments = !!props.attachments && props.attachments.length > 0;
      const hasText = !!unref(text) && unref(text).trim().length > 0;

      const innerContent =
        props.senderRole === "user" ? (
          <div class="max-w-[85%] animate-[fadeInMsg_0.25s_ease-out]">
            <div class="bg-primary-50 px-4 py-2.5 rounded-2xl rounded-br-md text-gray-800 text-sm font-normal font-['Inter']">
              {hasText ? (
                <Message
                  text={text}
                  options={{
                    inline: !!props.chunk,
                  }}
                />
              ) : null}
              {hasAttachments ? (
                <AttachmentList attachments={props.attachments!} />
              ) : null}
            </div>
          </div>
        ) : (
          <div class="w-full flex items-start gap-3 animate-[fadeInMsg_0.25s_ease-out]">
            {ImageComponent ? (
              <div class="hidden w-[33px] h-[33px] sm:flex items-center justify-center flex-shrink-0">
                <ImageComponent />
              </div>
            ) : null}
            <div class="flex-1 min-w-0 text-gray-800 text-sm leading-relaxed font-normal font-['Inter'] pt-1.5">
              {thinking.value ? (
                <ToolCallIndicator label={props.toolCall?.label ?? 'Thinking…'} />
              ) : (
                <Message
                  text={text}
                  stream={props.stream}
                  options={{
                    inline: !!props.chunk,
                  }}
                />
              )}
            </div>
          </div>
        );

      return (
        <div
          class={[
            "w-full flex",
            props.senderRole === "user" ? "justify-end" : "justify-start",
          ]}
        >
          {innerContent}
        </div>
      );
    };
  },
  { props: ["text", "image", "senderRole", "chunk", "thinking", "stream", "toolCall", "attachments"] },
);
